import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  AdModel,
  NotificationTypeEnum,
  FileKindEnum,
  AdImageData,
  AdSummary,
} from '@/types';
import { adsProvider } from '@/providers';
import { appSetNotification } from '@/store';

export const useAdsData = (ids: string[], currency = 'AED', pdf = false) => {
  const [adsData, setAdsData] = useState<AdSummary[]>([]);
  const [images, setImages] = useState<AdImageData[]>([]);
  const dispatch = useDispatch();

  const extractAdData = (ad: AdModel): AdSummary => {
    const {
      id,
      fakeId,
      nameEn,
      addressEn,
      defaultRentFrequency,
      activityKind,
      convertedPrice,
      convertedPriceYearly,
      convertedPriceMonthly,
      convertedPriceWeekly,
      convertedPriceDaily,
      convertedCurrency,
      floor,
      propertyKind,
      completionStatus,
      convertedArea,
      plotArea,
      convertedAreaUnits,
      baths,
      beds,
      description,
      toSeller,
      toDeveloper,
      handover,
      postHandover,
      transactionType,
      view,
      convertedSellingPrice,
      coordinatesLat,
      coordinatesLong,
      balcony,
      maidsRoom,
      sharedPool,
      builtInWardrobes,
      childrenPlayArea,
      sharedSpa,
      sharedGym,
      study,
      childrenPool,
      centralAc,
      coveredParking,
      walkInCloset,
      barbecueArea,
      viewOfWater,
      viewOfLandmark,
      petsAllowed,
      privateJacuzzi,
      concierge,
      partlyFurnished,
      unfurnished,
      publicParking,
      undergroundParking,
      kitchenAppliances,
      lobbyInBuilding,
      maidService,
      petsNotAllowed,
      privateGarden,
      privateGym,
      privatePool,
      security,
      tennisCourts,
      viewOfPark,
      viewOfCity,
      privateSpa,
      laundryFacility,
    } = ad;

    const features = {
      balcony,
      maidsRoom,
      sharedPool,
      builtInWardrobes,
      childrenPlayArea,
      sharedSpa,
      sharedGym,
      study,
      childrenPool,
      centralAc,
      coveredParking,
      walkInCloset,
      barbecueArea,
      viewOfWater,
      viewOfLandmark,
      petsAllowed,
      privateJacuzzi,
      concierge,
      partlyFurnished,
      unfurnished,
      publicParking,
      undergroundParking,
      kitchenAppliances,
      lobbyInBuilding,
      maidService,
      petsNotAllowed,
      privateGarden,
      privateGym,
      privatePool,
      security,
      tennisCourts,
      viewOfPark,
      viewOfCity,
      privateSpa,
      laundryFacility,
    }

    return {
      id,
      fakeId,
      nameEn,
      addressEn,
      defaultRentFrequency,
      activityKind,
      convertedPrice,
      convertedPriceYearly,
      convertedPriceMonthly,
      convertedPriceWeekly,
      convertedPriceDaily,
      convertedCurrency,
      floor,
      propertyKind,
      completionStatus,
      convertedArea,
      plotArea,
      convertedAreaUnits,
      baths,
      beds,
      description,
      toSeller,
      toDeveloper,
      handover,
      postHandover,
      transactionType,
      view,
      convertedSellingPrice,
      coordinatesLat,
      coordinatesLong,
      features,
    };
  };

  const fetchData = async () => {
    try {
      const responses = await Promise.all(
        ids.map((id) => adsProvider.getAdById(id, currency, pdf))
      );

      const validAds = responses
        .filter(({ ok }) => ok)
        .map(({ data }) => data as AdModel);

      const errors = responses.filter(({ ok }) => !ok);
      if (errors.length > 0) {
        const { message, status } = errors[0];
        dispatch(appSetNotification(NotificationTypeEnum.Error, message, status));
      }

      const trimmedAds = validAds.map(extractAdData);
      setAdsData(trimmedAds);

      const imageList: AdImageData[] = validAds.map((ad) => ({
        id: ad.id,
        name: ad.nameEn,
        images: ad.propertyAdMedia.filter((media) => media.kind === FileKindEnum.Image),
      }));

      setImages(imageList);
    } catch (error) {
      console.error('Unexpected error:', error);
    }
  };

  useEffect(() => {
    if (ids.length > 0) {
      fetchData();
    }
  }, [JSON.stringify(ids), currency]);

  return { adsData, images };
};
